import type { ProviderMigrationInventory } from "@/hooks/use-provider-migration";
import { inventoryAttribute } from "@/features/provider-migration/shared";

/**
 * Auxiliary vault names, admitted only when the source evidence is provably the snapshot the page
 * is displaying right now (ALL-7279). Vault IDs can repeat across snapshots, so a name is joined
 * to an exact provider resource ID only when:
 *
 * - the source read succeeded and is complete (`sourceReadable`, not `truncated`);
 * - the source is the vault-account category, read from the same sealed snapshot as the target
 *   (exact, case-sensitive identity — never normalised, never inferred);
 * - the row is a real `vault_account` with a nonblank exact `providerResourceId` and a name;
 * - that vault ID appears exactly once — every duplicate ID is ambiguous and is withheld, even
 *   when the names are identical (never last-write-wins).
 *
 * Anything unproven returns an empty map: no fallback name, no invented identity or snapshot.
 * This is a pure read-model helper — no calls, clocks, freshness inference or money arithmetic.
 */
export function buildSnapshotVaultNames(
  vaultInventory: ProviderMigrationInventory | null,
  targetSnapshotId: string | null | undefined,
  sourceReadable: boolean,
): Map<string, string> {
  const names = new Map<string, string>();
  if (!vaultInventory || !sourceReadable) return names;
  if (vaultInventory.truncated) return names;
  if (vaultInventory.category !== "VAULT_ACCOUNTS") return names;

  // Trim only to reject absence; the comparison itself is the exact raw identity.
  const sourceSnapshotId = vaultInventory.snapshotId;
  if (typeof sourceSnapshotId !== "string" || sourceSnapshotId.trim() === "") return names;
  if (typeof targetSnapshotId !== "string" || targetSnapshotId.trim() === "") return names;
  if (sourceSnapshotId !== targetSnapshotId) return names;

  // First pass: collect each vault ID's candidate name and mark every ID seen more than once.
  const candidateNames = new Map<string, string | null>();
  const ambiguousIds = new Set<string>();
  for (const item of vaultInventory.items) {
    if (item.resourceType !== "vault_account") continue;
    const vaultId = item.providerResourceId;
    if (typeof vaultId !== "string" || vaultId.trim() === "") continue;
    if (candidateNames.has(vaultId)) {
      ambiguousIds.add(vaultId);
      continue;
    }
    candidateNames.set(vaultId, inventoryAttribute(item, "name"));
  }

  // Second pass: emit only unambiguous IDs that carry an actual name.
  for (const [vaultId, name] of candidateNames) {
    if (name === null || ambiguousIds.has(vaultId)) continue;
    names.set(vaultId, name);
  }
  return names;
}
